import { defaultLanguage } from './consts';

import { ILangProvider, ILanguageResourcesCollection, ILanguageServer } from './interfaces';

export class LangProvider implements ILangProvider {
  private currentLanguageStr: string | null = null;
  private currentLanguageResources: ILanguageResourcesCollection | null = null;

  private readonly languageServer: ILanguageServer;

  constructor(languageServer: ILanguageServer, currentLanguage?: string) {
    this.languageServer = languageServer;
    if (currentLanguage) this.currentLanguage = currentLanguage;
  }

  asLanguage(language: string): ILangProvider {
    if (this.currentLanguageStr !== language) {
      return new LangProvider(this.languageServer, language);
    }
    return this;
  }

  get currentLanguage(): string {
    return this.currentLanguageStr ?? defaultLanguage;
  }

  set currentLanguage(value: string) {
    if (this.currentLanguageStr !== value) {
      this.currentLanguageStr = value;
      this.currentLanguageResources = this.languageServer.getLanguage(value);
    }
  }

  private get current(): ILanguageResourcesCollection {
    if (!this.currentLanguageResources) {
      this.currentLanguageResources = this.languageServer.getLanguage(this.currentLanguage);
    }
    return this.currentLanguageResources;
  }

  public getEntryByValue(value: string, resourceCollectionName?: string): string {
    if (value === '') {
      return '';
    }
    if (resourceCollectionName) {
      const resources = this.current.resources.find(resource => resource.name === resourceCollectionName);
      if (!resources) {
        return value;
      }
      const content = resources.getByValue(value)?.getSegment();
      if (content) return content.target ?? content.source;
      return value;
    }

    const globalContent = this.current.getByValue(value)?.getSegment();
    if (globalContent) {
      return globalContent.target ?? globalContent.source;
    }
    return value;
  }

  public getEntry(localizedId: string, segmentName: string | null = null): string {
    const langItem = this.current.get(localizedId);
    if (!langItem) {
      throw new Error(`Language Entry ${localizedId} not found.`);
    }

    const entry = langItem.getSegment(segmentName);
    if (!entry) {
      throw new Error(`Language Entry ${localizedId} not found by Segment ${segmentName ?? '__default__'}.`);
    }

    return entry.target ?? entry.source;
  }
}
